import errorSent from "~/utils/errors/errorSent";

import { clientApiCall, clientApiCallWithStatus } from "./apiCallWrapper";
import type {
  FormV2FieldsResponse,
  FormV2SubmitBody,
  FormV2SubmitResponse,
} from "./types/formsV2";

/** Коды полей, которые сервер пометил ошибкой — без значений (там телефон и ФИО). */
function getErrorFieldCodes(fields: unknown) {
  return Array.isArray(fields)
    ? fields.filter((f: any) => f?.ERROR).map((f: any) => f?.CODE)
    : undefined;
}

/** Клиентский API веб-форм Bitrix bxApi (/forms_v2/*). */
export default (_axios: any, config: any) => ({
  async getFields(
    code: string,
    reset = false,
  ): Promise<FormV2FieldsResponse | null> {
    const data = await clientApiCall<FormV2FieldsResponse>(
      `${config.API_URL}/forms_v2/getForm`,
      {
        api: "formsV2.getFields",
        // Форму боту всё равно не заполнять, а в GlitchTip их запросы шумят:
        // из 23 таймаутов getFields 6 приехали от YandexBot.
        skipForRobots: true,
        // Без формы заявку не отправить, поэтому сообщаем о любом сбое, в том
        // числе о 403: 15.10.2026 посетителю msk.farkop.ru форму отсекло ещё до
        // нашего фронта, а видно это было только по неудачной отправке.
        critical: true,
        params: {
          code,
          ...(reset ? { clear_cache: true } : {}),
        },
      },
    );
    // Сервер ответил, но формы нет («Форма не найдена») — посетитель видит пустой попап
    if (data && (data as any).success === false) {
      errorSent("formError", "formLoadRejected", {
        code,
        message: (data as any).message,
      });
    }
    return data;
  },

  async sentForm(
    code: string,
    body: FormV2SubmitBody = {},
  ): Promise<FormV2SubmitResponse | null> {
    const { data, failed } =
      await clientApiCallWithStatus<FormV2SubmitResponse>(
        `${config.API_URL}/forms_v2/sentForm`,
        {
          api: "formsV2.sentForm",
          critical: true,
          lead: true,
          method: "post",
          params: body,
          axiosConfig: {
            withCredentials: true,
            // Заявка сохраняется до отправки в Б24 и Calltouch, которые идут
            // синхронно в том же запросе. С таймаутом 20 с клиент видел ошибку по
            // уже принятой заявке и отправлял её повторно.
            timeout: 60000,
            params: { code },
          },
        },
      );
    // failed — запрос не выполнен, об этом уже сообщил clientApiCallWithStatus.
    // Иначе сервер ответил, но заявку не принял. Пустой ответ (Битрикс отдаёт
    // null, напр. без cookie PHPSESSID) — заявка потеряна, а форма молча ничего
    // не показывает: это fatal. «Заполните обязательные поля» и т.п. — warning.
    if (!failed && !data) {
      errorSent("formError", "formEmptyResponse", { code });
    } else if (!failed && !data?.success) {
      errorSent("formError", "formRejected", {
        code,
        message: data?.message || null,
        errorFields: getErrorFieldCodes(data?.fields),
      });
    }
    return data;
  },
});
